'use client';

import React, { useState, useEffect } from 'react';
import PartnerCard from './PartnerCard';
import FilterSection from './FilterSection';
import PartnerModal from './PartnerModal';
import PartnershipApplicationForm from './PartnershipApplicationForm';
import StatsSection from './StatsSection';
import Icon from '@/components/ui/AppIcon';

interface Partner {
  id: number;
  name: string;
  type: string;
  logo: string;
  logoAlt: string;
  description: string;
  country: string;
  established: string;
  website: string;
  impact: string;
  fullDescription: string;
  achievements: string[];
  contactEmail: string;
  focusAreas: string[];
  region: string;
}

const PartnerNetworkInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockPartners: Partner[] = [
  {
    id: 1,
    name: 'United Nations Development Programme',
    type: 'UN Agency',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_159858003-1764694158049.png",
    logoAlt: 'UNDP logo with blue and white UN emblem on professional background',
    description:
    'Leading UN agency working to eradicate poverty and reduce inequalities through sustainable development.',
    country: 'Global',
    established: '1965',
    website: 'https://www.undp.org',
    impact: '15M+ people reached through joint peace initiatives',
    fullDescription:
    'The United Nations Development Programme partners with SIG Day to integrate emotional healing and forgiveness frameworks into community development programs across 170 countries. Our collaboration focuses on post-conflict reconstruction, youth empowerment, and sustainable peace-building through grassroots transformation.',
    achievements: [
    'Integrated SIG Day principles into 45 national development programs',
    'Trained 12,000+ community leaders in forgiveness-based conflict resolution',
    'Supported 230 local peace festivals across conflict-affected regions',
    'Published joint research on emotional healing and sustainable development'],

    contactEmail: 'partnerships@undp.org',
    focusAreas: [
    'Peace Education',
    'Community Development',
    'Policy Advocacy',
    'Research & Evidence'],

    region: 'Global'
  },
  {
    id: 2,
    name: 'African Union Commission',
    type: 'Government',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1958b0596-1764749478976.png",
    logoAlt: 'African Union emblem with green and gold colors on official background',
    description:
    'Continental body promoting unity, peace, and development across 55 African member states.',
    country: 'Pan-African',
    established: '2002',
    website: 'https://au.int',
    impact: '8M+ participants in AU-endorsed SIG Day celebrations',
    fullDescription:
    'The African Union Commission recognizes SIG Day as a strategic partner in advancing Agenda 2063 and Silencing the Guns initiative. Our partnership focuses on youth-led peace movements, traditional reconciliation practices, and continental solidarity through shared healing experiences.',
    achievements: [
    'Official recognition of SIG Day across 42 African nations',
    'Integrated forgiveness education into AU Youth Volunteer Corps',
    'Co-hosted 18 regional peace summits with SIG Day framework',
    'Established AU-SIG Day Fellowship for emerging peace leaders'],

    contactEmail: 'peace@africa-union.org',
    focusAreas: [
    'Policy Advocacy',
    'Youth Engagement',
    'Cultural Exchange',
    'Conflict Resolution'],

    region: 'Africa'
  },
  {
    id: 3,
    name: 'World Council of Churches',
    type: 'Faith-Based',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14997b53f-1767663865542.png",
    logoAlt: 'World Council of Churches cross symbol with dove on spiritual background',
    description:
    'Fellowship of 352 churches representing over 580 million Christians worldwide, promoting unity and peace.',
    country: 'Switzerland',
    established: '1948',
    website: 'https://www.oikoumene.org',
    impact: '25M+ faithful engaged through interfaith SIG Day programs',
    fullDescription:
    'The World Council of Churches partners with SIG Day to bridge theological traditions through shared commitment to forgiveness, reconciliation, and peace-building. Our collaboration emphasizes interfaith dialogue, spiritual healing practices, and faith-based community transformation.',
    achievements: [
    'Mobilized 15,000+ congregations for annual SIG Day observance',
    'Developed interfaith forgiveness curriculum used in 67 countries',
    'Facilitated 120+ interfaith healing dialogues in conflict zones',
    'Published joint theological framework on forgiveness and justice'],

    contactEmail: 'peace@wcc-coe.org',
    focusAreas: [
    'Peace Education',
    'Cultural Exchange',
    'Community Development',
    'Humanitarian Aid'],

    region: 'Europe'
  },
  {
    id: 4,
    name: 'Stanford Peace Innovation Lab',
    type: 'University',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11a78e4ea-1767244144058.png",
    logoAlt: 'Stanford University campus building with red tile roof and academic architecture',
    description:
    'Leading research center advancing peace through technology, design thinking, and evidence-based innovation.',
    country: 'United States',
    established: '2015',
    website: 'https://peaceinnovation.stanford.edu',
    impact: '450+ research papers on forgiveness and peace-building',
    fullDescription:
    'Stanford Peace Innovation Lab collaborates with SIG Day to provide rigorous academic research, measurement frameworks, and evidence-based validation of forgiveness-centered peace-building approaches. Our partnership advances the science of emotional healing and conflict transformation.',
    achievements: [
    'Published 12 peer-reviewed studies on SIG Day impact',
    'Developed global measurement framework for forgiveness initiatives',
    'Trained 3,000+ researchers in peace innovation methodologies',
    'Established SIG Day Research Fellowship for doctoral candidates'],

    contactEmail: 'peace@stanford.edu',
    focusAreas: ['Research & Evidence', 'Peace Education', 'Policy Advocacy'],
    region: 'North America'
  },
  {
    id: 5,
    name: 'International Committee of the Red Cross',
    type: 'NGO',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1aec95da2-1766766355059.png",
    logoAlt: 'Red Cross emblem with white background and humanitarian symbol',
    description:
    'Humanitarian organization providing protection and assistance to victims of armed conflict and violence.',
    country: 'Switzerland',
    established: '1863',
    website: 'https://www.icrc.org',
    impact: '3M+ conflict-affected individuals reached through joint programs',
    fullDescription:
    'The International Committee of the Red Cross partners with SIG Day to integrate emotional healing into humanitarian response, particularly in post-conflict settings. Our collaboration focuses on trauma recovery, family reunification, and community reconciliation in war-affected regions.',
    achievements: [
    'Integrated SIG Day principles into 28 field operations',
    'Trained 5,000+ humanitarian workers in forgiveness-based trauma care',
    'Supported 180 community healing initiatives in conflict zones',
    'Developed joint protocols for emotional first aid in emergencies'],

    contactEmail: 'partnerships@icrc.org',
    focusAreas: [
    'Humanitarian Aid',
    'Conflict Resolution',
    'Community Development',
    'Peace Education'],

    region: 'Europe'
  },
  {
    id: 6,
    name: 'Unilever Foundation',
    type: 'Corporate',
    logo: "https://images.unsplash.com/photo-1635071247661-ede154087b09",
    logoAlt: 'Modern corporate office building with glass facade and professional architecture',
    description:
    'Corporate foundation advancing sustainable living and social impact through business leadership.',
    country: 'United Kingdom',
    established: '2000',
    website: 'https://www.unilever.com/sustainable-living',
    impact: '$8M+ invested in SIG Day community programs',
    fullDescription:
    'Unilever Foundation partners with SIG Day to demonstrate corporate leadership in peace-building and social cohesion. Our collaboration focuses on employee engagement, supply chain peace initiatives, and sustainable community development through forgiveness-based approaches.',
    achievements: [
    'Mobilized 50,000+ employees for SIG Day volunteering',
    'Funded 75 community peace projects in operational regions',
    'Integrated forgiveness training into leadership development',
    'Established annual SIG Day Corporate Peace Award'],

    contactEmail: 'foundation@unilever.com',
    focusAreas: [
    'Community Development',
    'Youth Engagement',
    'Humanitarian Aid',
    'Cultural Exchange'],

    region: 'Europe'
  },
  {
    id: 7,
    name: 'Ministry of Peace - Costa Rica',
    type: 'Government',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11abd1512-1769685070125.png",
    logoAlt: 'Costa Rica national emblem with blue, white and red colors on official background',
    description:
    'National government ministry dedicated to peace education, conflict resolution, and social harmony.',
    country: 'Costa Rica',
    established: '2009',
    website: 'https://www.mideplan.go.cr',
    impact: '2.5M+ citizens engaged in national SIG Day programs',
    fullDescription:
    'Costa Rica\'s Ministry of Peace partners with SIG Day to advance the nation\'s constitutional commitment to peace through forgiveness education, community healing, and cultural transformation. Our collaboration positions Costa Rica as a global model for peace-centered governance.',
    achievements: [
    'Declared SIG Day as national observance with official recognition',
    'Integrated forgiveness curriculum into national education system',
    'Established 120 community peace centers using SIG Day framework',
    'Hosted first-ever National Forgiveness Summit with SIG Day'],

    contactEmail: 'paz@mideplan.go.cr',
    focusAreas: [
    'Policy Advocacy',
    'Peace Education',
    'Community Development',
    'Cultural Exchange'],

    region: 'South America'
  },
  {
    id: 8,
    name: 'Islamic Relief Worldwide',
    type: 'Faith-Based',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8a54a47-1769685068795.png",
    logoAlt: 'Islamic Relief logo with crescent moon symbol on humanitarian background',
    description:
    'Independent humanitarian organization providing relief and development in over 40 countries.',
    country: 'United Kingdom',
    established: '1984',
    website: 'https://www.islamic-relief.org',
    impact: '6M+ beneficiaries in Islamic Relief-SIG Day joint programs',
    fullDescription:
    'Islamic Relief Worldwide partners with SIG Day to integrate Islamic principles of forgiveness, mercy, and reconciliation into humanitarian programming. Our collaboration emphasizes interfaith solidarity, community healing, and faith-based peace-building across Muslim-majority regions.',
    achievements: [
    'Implemented SIG Day programs in 35 countries',
    'Trained 8,000+ community leaders in Islamic forgiveness frameworks',
    'Supported 200+ interfaith healing dialogues',
    'Developed Islamic theological resources for forgiveness education'],

    contactEmail: 'partnerships@islamic-relief.org',
    focusAreas: [
    'Humanitarian Aid',
    'Peace Education',
    'Community Development',
    'Cultural Exchange'],

    region: 'Europe'
  },
  {
    id: 9,
    name: 'University of Cape Town Peace Centre',
    type: 'University',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a60aedb7-1766821057976.png",
    logoAlt: 'University of Cape Town campus with Table Mountain backdrop and academic buildings',
    description:
    'Leading African research center advancing peace studies, conflict transformation, and social justice.',
    country: 'South Africa',
    established: '1995',
    website: 'https://www.uct.ac.za/peace',
    impact: '2,000+ peace practitioners trained through joint programs',
    fullDescription:
    'University of Cape Town Peace Centre partners with SIG Day to advance African scholarship on forgiveness, reconciliation, and post-conflict healing. Our collaboration focuses on indigenous peace practices, youth leadership development, and evidence-based peace-building research.',
    achievements: [
    'Established SIG Day Research Chair in Forgiveness Studies',
    'Published 25+ academic papers on African forgiveness traditions',
    'Trained 500+ graduate students in SIG Day methodologies',
    'Developed African Peace Practitioners Certification Program'],

    contactEmail: 'peace@uct.ac.za',
    focusAreas: [
    'Research & Evidence',
    'Peace Education',
    'Youth Engagement',
    'Cultural Exchange'],

    region: 'Africa'
  },
  {
    id: 10,
    name: 'Oxfam International',
    type: 'NGO',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1636670cc-1769685071099.png",
    logoAlt: 'Oxfam logo with green and black colors on humanitarian background',
    description:
    'Global confederation fighting inequality and poverty through sustainable solutions and advocacy.',
    country: 'United Kingdom',
    established: '1995',
    website: 'https://www.oxfam.org',
    impact: '10M+ people reached through Oxfam-SIG Day initiatives',
    fullDescription:
    'Oxfam International partners with SIG Day to address the emotional dimensions of poverty, inequality, and social injustice. Our collaboration integrates forgiveness-based healing into community development, women\'s empowerment, and conflict-sensitive programming.',
    achievements: [
    'Integrated SIG Day principles into 60 country programs',
    'Trained 15,000+ community organizers in forgiveness methodologies',
    'Supported 300+ women-led peace initiatives',
    'Published joint research on healing and economic empowerment'],

    contactEmail: 'partnerships@oxfam.org',
    focusAreas: [
    'Community Development',
    'Humanitarian Aid',
    'Policy Advocacy',
    'Youth Engagement'],

    region: 'Europe'
  },
  {
    id: 11,
    name: 'Buddhist Peace Fellowship',
    type: 'Faith-Based',
    logo: "https://images.unsplash.com/photo-1617637881618-c4d759ca41d4",
    logoAlt: 'Buddhist dharma wheel symbol with golden color on spiritual background',
    description:
    'International network applying Buddhist wisdom to contemporary social, political, and environmental issues.',
    country: 'United States',
    established: '1978',
    website: 'https://www.buddhistpeacefellowship.org',
    impact: '1.5M+ practitioners engaged in mindful forgiveness practices',
    fullDescription:
    'Buddhist Peace Fellowship partners with SIG Day to integrate Buddhist teachings on compassion, mindfulness, and loving-kindness into global peace-building efforts. Our collaboration emphasizes contemplative practices, interfaith dialogue, and spiritual approaches to conflict transformation.',
    achievements: [
    'Developed Buddhist forgiveness meditation curriculum',
    'Facilitated 80+ interfaith contemplative peace retreats',
    'Trained 2,000+ meditation teachers in SIG Day principles',
    'Published joint resources on mindfulness and forgiveness'],

    contactEmail: 'peace@bpf.org',
    focusAreas: [
    'Peace Education',
    'Cultural Exchange',
    'Community Development',
    'Conflict Resolution'],

    region: 'North America'
  },
  {
    id: 12,
    name: 'Australian National University Peace Institute',
    type: 'University',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4a59d46-1764748284552.png",
    logoAlt: 'Australian National University campus with modern architecture and academic buildings',
    description:
    'Premier research institute advancing peace and security studies in the Asia-Pacific region.',
    country: 'Australia',
    established: '2017',
    website: 'https://www.anu.edu.au/peace',
    impact: '800+ policy makers influenced through joint research',
    fullDescription:
    'Australian National University Peace Institute partners with SIG Day to advance regional peace-building research, policy development, and practitioner training. Our collaboration focuses on Pacific Island peace initiatives, indigenous reconciliation, and evidence-based forgiveness interventions.',
    achievements: [
    'Published 15+ policy briefs on forgiveness and regional security',
    'Trained 300+ government officials in SIG Day frameworks',
    'Established Pacific Peace Scholars Program',
    'Developed regional measurement tools for peace initiatives'],

    contactEmail: 'peace@anu.edu.au',
    focusAreas: [
    'Research & Evidence',
    'Policy Advocacy',
    'Peace Education',
    'Cultural Exchange'],

    region: 'Oceania'
  }];


  const filteredPartners = mockPartners.filter((partner) => {
    const matchesType = selectedType === 'All Types' || partner.type === selectedType;
    const matchesRegion = selectedRegion === 'All Regions' || partner.region === selectedRegion;
    const matchesSearch =
    searchQuery === '' ||
    partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    partner.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesType && matchesRegion && matchesSearch;
  });

  const handleViewDetails = (id: number) => {
    const partner = mockPartners.find((p) => p.id === id);
    if (partner) {
      setSelectedPartner(partner);
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="h-64 bg-muted rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-headline font-bold text-foreground mb-4">
            Partner Network
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Building global peace through strategic collaborations with governments, NGOs,
            faith-based organizations, and academic institutions worldwide.
          </p>
        </div>

        <StatsSection />

        <FilterSection
          selectedType={selectedType}
          selectedRegion={selectedRegion}
          searchQuery={searchQuery}
          onTypeChange={setSelectedType}
          onRegionChange={setSelectedRegion}
          onSearchChange={setSearchQuery} />


        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-headline font-bold text-foreground">
              Our Partners ({filteredPartners.length})
            </h2>
            <button
              onClick={() => setShowApplicationForm(!showApplicationForm)}
              className="px-6 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold hover:scale-105 transition-all duration-300 flex items-center">

              <Icon name="PlusIcon" size={20} className="mr-2" />
              {showApplicationForm ? 'View Partners' : 'Become a Partner'}
            </button>
          </div>

          {showApplicationForm ?
          <PartnershipApplicationForm /> :

          <>
              {filteredPartners.length === 0 ?
            <div className="bg-card rounded-lg shadow-md p-12 text-center">
                  <Icon
                name="MagnifyingGlassIcon"
                size={64}
                className="mx-auto mb-4 text-muted-foreground" />

                  <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
                    No Partners Found
                  </h3>
                  <p className="text-muted-foreground">
                    Try adjusting your filters or search query to find partners.
                  </p>
                </div> :

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPartners.map((partner) =>
              <PartnerCard
                key={partner.id}
                partner={partner}
                onViewDetails={handleViewDetails} />

              )}
                </div>
            }
            </>
          }
        </div>

        <div className="bg-primary/10 rounded-lg p-8 text-center">
          <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
            Join Our Global Network
          </h2>
          <p className="text-lg text-foreground mb-6 max-w-2xl mx-auto">
            Partner with SIG Day to amplify your peace-building impact and connect with a global
            movement dedicated to forgiveness, healing, and transformation.
          </p>
          <button
            onClick={() => setShowApplicationForm(true)}
            className="px-8 py-4 bg-accent text-accent-foreground rounded-md font-cta text-lg font-bold hover:scale-105 transition-all duration-300 shadow-md pulse-gentle">

            Start Partnership Application
          </button>
        </div>
      </div>

      {selectedPartner &&
      <PartnerModal partner={selectedPartner} onClose={() => setSelectedPartner(null)} />
      }
    </div>);

};

export default PartnerNetworkInteractive;